/**
 * Deals Stack — draggable card stack with nudge affordance.
 * Pointer events run in every browser. Top card follows pointer;
 * past ±120px threshold it flies off and recycles to the back.
 * Periodic nudge shake teaches draggability — stops on first touch.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class DealsStackComponent extends Component {
  requiredRefs = ['stage'];
  #io = null;
  #nudgeTimer = null;
  #start = null;
  #interacted = false;

  connectedCallback() {
    super.connectedCallback();

    // IO reveal
    const reveals = this.querySelectorAll('.reveal');
    if (prefersReducedMotion()) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      this.#io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); this.#io?.unobserve(e.target); }
        }
      }, { rootMargin: '0px 0px -10% 0px' });
      reveals.forEach((el) => this.#io.observe(el));
    }

    this.#bindCards();
    this.#startNudge();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
    if (this.#nudgeTimer) clearInterval(this.#nudgeTimer);
  }

  #getCards() {
    return Array.from(this.refs.stage?.querySelectorAll('.ds__card') ?? []);
  }

  #bindCards() {
    const cards = this.#getCards();
    cards.forEach((card) => {
      card.addEventListener('pointerdown', this.#onDown);
    });
  }

  #onDown = (e) => {
    if (!e.currentTarget.classList.contains('ds__card--top')) return;
    const card = e.currentTarget;
    card.setPointerCapture(e.pointerId);
    card.classList.add('dragging');
    this.#start = { x: e.clientX, card };
    card.addEventListener('pointermove', this.#onMove);
    card.addEventListener('pointerup', this.#onUp, { once: true });
    card.addEventListener('pointercancel', this.#onUp, { once: true });

    if (!this.#interacted) {
      this.#interacted = true;
      if (this.#nudgeTimer) clearInterval(this.#nudgeTimer);
    }
  };

  #onMove = (e) => {
    if (!this.#start) return;
    const dx = e.clientX - this.#start.x;
    const rot = dx / 20;
    this.#start.card.style.transform = `translateX(${dx}px) rotate(${rot}deg)`;
  };

  #onUp = (e) => {
    if (!this.#start) return;
    const { card } = this.#start;
    const dx = e.clientX - this.#start.x;
    this.#start = null;
    card.removeEventListener('pointermove', this.#onMove);
    card.classList.remove('dragging');

    if (Math.abs(dx) > 120) {
      this.#flyOff(card, dx > 0 ? 'right' : 'left');
    } else {
      card.style.transform = '';
    }
  };

  #flyOff(card, direction) {
    const cls = direction === 'right' ? 'fly-right' : 'fly-left';
    card.classList.add(cls, 'ds__card--flying');
    card.classList.remove('ds__card--top');
    card.querySelector('.ds__card-link')?.setAttribute('tabindex', '-1');

    card.addEventListener('animationend', () => {
      card.classList.remove(cls, 'ds__card--flying');
      card.style.transform = '';
      card.style.zIndex = '';
      // Move to back
      const stage = this.refs.stage;
      stage.appendChild(card);
      this.#restack();
    }, { once: true });
  }

  #restack() {
    const cards = this.#getCards();
    const total = cards.length;
    cards.forEach((card, i) => {
      card.classList.toggle('ds__card--top', i === 0);
      card.style.setProperty('--depth', i);
      card.style.zIndex = total - i;
      card.querySelector('.ds__card-link')?.setAttribute('tabindex', i === 0 ? '0' : '-1');
    });
    if (!this.#interacted) this.#startNudge();
  }

  #startNudge() {
    if (prefersReducedMotion() || this.#interacted) return;
    const play = () => {
      if (this.#interacted) return;
      const top = this.refs.stage?.querySelector('.ds__card--top');
      if (!top) return;
      top.classList.remove('nudge');
      void top.offsetWidth;
      top.classList.add('nudge');
    };
    // Fire when section enters view
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) { play(); io.disconnect(); }
    });
    io.observe(this);
    // Then repeat every 4s
    this.#nudgeTimer = setInterval(play, 4000);
  }
}

if (!customElements.get('deals-stack-component')) {
  customElements.define('deals-stack-component', DealsStackComponent);
}
